// Pure interest math for offerings shown in the purchase flow. Kept framework-free so the same
// numbers are produced everywhere anticipated returns are displayed (PF_TermAndYield, UpsellCard)
// — these formulas used to be duplicated per-component and drifted.
//
// Conventions match the offering record shape: rates are PERCENT (offering.interest_rate, e.g. 7
// for 7%) and terms are whole MONTHS (offering.bond_term).

const MONTHS_PER_YEAR = 12;
const DAYS_PER_YEAR = 365;

// Days in each Set Put Interval, used for the daily compounding basis on short-term (Flex) notes.
// Keyed by bond_term in months.
export const SHORT_TERM_COMPOUNDING_DAYS = { 3: 90, 6: 181, 9: 272, 12: 363, 18: 545 };
const DEFAULT_SHORT_TERM_DAYS = 90;

/**
 * Coerce the three offering inputs, returning null when any is missing or non-numeric so callers
 * can render a zero instead of "$NaN".
 */
function coerceTerms(principal, annualRatePercent, termMonths) {
  const p = Number(principal);
  const rate = Number(annualRatePercent) / 100;
  const months = Number(termMonths);
  if (!Number.isFinite(p) || !Number.isFinite(rate) || !Number.isFinite(months)) return null;
  if (!p || !rate || !months) return null;
  return { p, rate, months };
}

/**
 * One month's interest payment for an "Interest Paid Monthly" (simple interest) investment.
 * This is the monthly-payment basis: an annual rate divided into 12 equal payments.
 */
export function monthlyInterestPayment(principal, annualRatePercent) {
  const p = Number(principal);
  const rate = Number(annualRatePercent) / 100;
  if (!Number.isFinite(p) || !Number.isFinite(rate)) return 0;
  return p * (rate / MONTHS_PER_YEAR);
}

/**
 * Total value at maturity for a compounded investment, principal included.
 *
 * Short-term (Flex) notes compound DAILY over the day count of the selected Set Put Interval;
 * every other offering compounds monthly over its term. Returns the bare principal when inputs
 * are incomplete.
 */
export function maturityValue(
  principal,
  annualRatePercent,
  termMonths,
  { isShortTerm = false } = {}
) {
  const terms = coerceTerms(principal, annualRatePercent, termMonths);
  if (!terms) return Number(principal) || 0;
  const { p, rate, months } = terms;

  if (isShortTerm) {
    const days = SHORT_TERM_COMPOUNDING_DAYS[months] ?? DEFAULT_SHORT_TERM_DAYS;
    return p * Math.pow(1 + rate / DAYS_PER_YEAR, days);
  }
  return p * Math.pow(1 + rate / MONTHS_PER_YEAR, months);
}

/**
 * Interest earned over the full term, excluding principal — the "anticipated returns" figure.
 *
 * Compounded investments earn maturity value less principal (daily basis for short-term notes,
 * see maturityValue). Simple interest earns the monthly payment once per month of the term, on
 * the same monthly basis for every offering — short-term notes are NOT special-cased here.
 */
export function totalInterest(
  principal,
  annualRatePercent,
  termMonths,
  { compounded = false, isShortTerm = false } = {}
) {
  const terms = coerceTerms(principal, annualRatePercent, termMonths);
  if (!terms) return 0;

  if (compounded) {
    return maturityValue(principal, annualRatePercent, termMonths, { isShortTerm }) - terms.p;
  }
  return monthlyInterestPayment(principal, annualRatePercent) * terms.months;
}
